// src/components/dashboard/KpiCard.js
import React from 'react';
import { TrendingUp, TrendingDown, Calendar, Target, CheckCircle2, XCircle } from 'lucide-react';

/**
 * KPI Card Component
 * Displays a key performance indicator with value, label, icon, and trend
 *
 * @param {string} label - The KPI label
 * @param {number} value - The main KPI value
 * @param {number} trend - Percentage change vs previous period (can be positive or negative)
 * @param {string} icon - Icon name (calendar, target, check, x)
 * @param {string} suffix - Optional suffix for the value (e.g., '%')
 * @param {string} color - Color theme (blue, green, purple, orange)
 */
const KpiCard = ({
  label,
  value,
  trend,
  icon = 'calendar',
  suffix = '',
  color = 'blue'
}) => {
  // Icon mapping
  const iconMap = {
    calendar: Calendar,
    target: Target,
    check: CheckCircle2,
    x: XCircle
  };

  const IconComponent = iconMap[icon] || Calendar;

  // Color schemes - light/dark mode aware
  const colorSchemes = {
    blue: {
      bg: 'bg-blue-100 dark:bg-blue-500/10',
      text: 'text-blue-600 dark:text-blue-400',
      icon: 'text-blue-600 dark:text-blue-400',
      border: 'border-blue-200 dark:border-blue-500/20'
    },
    green: {
      bg: 'bg-green-100 dark:bg-green-500/10',
      text: 'text-green-600 dark:text-green-400',
      icon: 'text-green-600 dark:text-green-400',
      border: 'border-green-200 dark:border-green-500/20'
    },
    purple: {
      bg: 'bg-purple-100 dark:bg-purple-500/10',
      text: 'text-purple-600 dark:text-purple-400',
      icon: 'text-purple-600 dark:text-purple-400',
      border: 'border-purple-200 dark:border-purple-500/20'
    },
    orange: {
      bg: 'bg-orange-100 dark:bg-orange-500/10',
      text: 'text-orange-600 dark:text-orange-400',
      icon: 'text-orange-600 dark:text-orange-400',
      border: 'border-orange-200 dark:border-orange-500/20'
    }
  };

  const scheme = colorSchemes[color] || colorSchemes.blue;

  return (
    <div className={`${scheme.bg} border ${scheme.border} rounded-2xl p-4 lg:p-6`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${scheme.bg}`}>
          <IconComponent className={`w-5 h-5 lg:w-6 lg:h-6 ${scheme.icon}`} />
        </div>
        {trend !== undefined && trend !== null && (
          <div className={`flex items-center gap-1 text-xs ${
            trend > 0 ? 'text-green-400' : trend < 0 ? 'text-red-400' : 'text-app-text-secondary-light dark:text-app-text-secondary'
          }`}>
            {trend > 0 ? (
              <TrendingUp className="w-3 h-3" />
            ) : trend < 0 ? (
              <TrendingDown className="w-3 h-3" />
            ) : null}
            <span className="font-semibold">
              {trend > 0 ? '+' : ''}{trend}%
            </span>
          </div>
        )}
      </div>

      <div className="space-y-1">
        <div className={`text-3xl lg:text-4xl font-bold ${scheme.text}`}>
          {value}{suffix}
        </div>
        <div className="text-xs lg:text-sm text-app-text-secondary-light dark:text-app-text-secondary font-medium">
          {label}
        </div>
      </div>
    </div>
  );
};

export default KpiCard;
